<script>
/* Imports */
import { defineAsyncComponent } from 'vue';
import { useProductOptions } from '@garmin/ui-product-ems-filters';
import {
  mapActions,
  mapGetters,
} from 'vuex';
import VueCookies from 'vue-cookies';
import { PcRenderPromo } from '@garmin/components-product';

/* Analytics */
import {
  sanitizeAnalyticsString,
  sendAnalyticsEvent,
  sendAnalyticsPageView,
  getCategory3Data,
  getSelectedEmsOption,
  getSelectedEmsOptionIndex,
} from '../helpers/ga/analytics.js';

/* Helpers */
import {
  breakpoints,
  convertImageSource,
  suppressRelatedVideos,
} from '../helpers/mainHelpers.js';

/* Containers */
import Interstitial from '../containers/Interstitial.vue';

/* Components */
import CloudinaryProductGallery from '../containers/CloudinaryProductGallery.vue';
import ProductInfo from '../containers/ProductInfo.vue';
import OldProductInfo from '../containers/OldProductInfo.vue';
import Breadcrumbs from '../components/Breadcrumbs.vue';
import ATCButton from '../components/ATCButton.vue';
import FakeATCButton from '../components/FakeATCButton.vue';

/* Composables */
import { useScreenResize } from '../composables/useScreenResize.js';
import { useGenericAnalyticsDataHelper } from '../composables/useAnalyticsData.js';
import ProductMediaGallery from '../containers/ProductMediaGallery.vue';

/* Components - Async -> Lazy Load */
const Subnav = defineAsyncComponent({
  loader: () => import('../components/Subnav.vue'),
});

export default {
  name: 'App',
  components: {
    ProductInfo,
    OldProductInfo,
    CloudinaryProductGallery,
    Breadcrumbs,
    Subnav,
    PcRenderPromo,
    Interstitial,
    ATCButton,
    FakeATCButton,
    ProductMediaGallery,
  },
  setup() {
    /* Initialize Composables */
    const { genericAnalyticsData } = useGenericAnalyticsDataHelper();
    const productOptions = useProductOptions();
    const { screenWidth } = useScreenResize();

    return {
      genericAnalyticsData,
      productOptions,
      screenWidth,
    };
  },
  data() {
    return {
      initialLoad: true,
      initialHash: '',
      defaultActiveTab: '',
      enableEMSRedesign: window?.envSettings?.ITFE_PRODUCT_PAGE_ENABLE_EMS_REDESIGN || false,
      enableNewMediaGallery: window?.envSettings?.ITFE_PRODUCT_PAGE_ENABLE_NEW_MEDIA_GALLERY || false,
    };
  },
  computed: {
    ...mapGetters([
      'getActiveProductTabs',
      'getSupplimentaryDisclaimers',
      'getPartNumber',
      'getProductActiveTabId',
      'getDefaultProductTab',
      'getIsActiveProductTab',
      'getLocale',
      'getProductId',
      'getProductVariation',
      'getBreadCrumbs',
      'getDefaultSku',
      'getPromoBanner',
      'getOverviewContainsDisclaimers',
      'getInterstitialIsVisible',
      'getInterstitialProductPvName',
      'getInterstitialProductImage',
      'getInterstitialProductAltText',
      'getPriceData',
      'getSeoAttributes',
      'getMarketCategoryGlobal',
      'getProductCategoryGlobal',
      'getCountryCode',
      'getOptionsList',
      'getOptionListByType',
      'getGlobalProductName',
      'getActiveCompatibleDevices',
      'getProductUrl',
      'getCustomerGroups',
      'getHashIsValidProductTab',
      'getProductTabsAreSet',
      'getProductName',
      'getCloudinaryAssets',
      'getCustomerImages',
      'getVideosURLs',
      'getCloudinaryCloudName',
      'getCloudinarySecureDistribution',
      'getBuyActionHref',
      'getBuyActionText',
      'getShowStickyAddToCartBtn',
      'getPinStickyATCButton',
      'getFooterVisibleHeight',
    ]),
    isMobileView() {
      return this.screenWidth <= breakpoints['mobile-m'];
    },
    isDesktopView() {
      return this.screenWidth >= breakpoints['desktop-s'];
    },
    enableStickyAddToCartBtn() {
      return this.getShowStickyAddToCartBtn && this.isMobileView;
    },
    enableStickyFakeAddToCartBtn() {
      return Boolean(this.enableStickyAddToCartBtn && document.getElementById('js__cta__bundle')?.querySelector('a'));
    },
    shouldPinStickyATCButton() {
      return this.getPinStickyATCButton && this.isMobileView;
    },
    pinnedToFooterStyle() {
      return this.shouldPinStickyATCButton ? {
        position: 'fixed',
        bottom: `${this.getFooterVisibleHeight}px`,
      } : '';
    },
    stickyAtcButtonClass() {
      return {
        'app__product__sticky-atc-button': true,
        'app__product__sticky-atc-button--hidden': !this.enableStickyAddToCartBtn,
      };
    },
    stickyFakeAtcButtonClass() {
      return {
        'app__product__sticky-fake-atc-button': true,
        'app__product__sticky-fake-atc-button--hidden': !this.enableStickyFakeAddToCartBtn,
      };
    },
    subNavLabels() {
      return this.getActiveProductTabs.map((product) => product.title);
    },
    subNavContent() {
      // If there are disclaimers present in the overview bootstrap response, we append a disclaimer block in the existing wrapper
      const appendDisclaimers = this.getOverviewContainsDisclaimers && this.getSupplimentaryDisclaimers.length;

      return this.getActiveProductTabs.map((product) => {
        let tabContent = product.content;
        if (product.id === 'overview' && appendDisclaimers) {
          tabContent = this.appendDisclaimerToOverviewTab(tabContent);
        }
        // Suppress unwanted related videos of all embedded Youtube videos
        const modifiedTabContent = suppressRelatedVideos(tabContent);

        return modifiedTabContent;
      });
    },
    subNavIds() {
      return this.getActiveProductTabs.map((product) => product.id);
    },
    showMobileBanner() {
      return !this.isDesktopView && this.getPromoBanner;
    },
    // (price with discount)
    productPrice() {
      return this.getPriceData?.salePrice?.price ?? this.getPriceData?.listPrice?.price ?? '';
    },
    discount() {
      return this.getPriceData?.savings?.price ?? '';
    },
    currencyCode() {
      return window?.utag_data?.currency_code;
    },
    breadCrumbAnalyticsData() {
      return {
        productName: sanitizeAnalyticsString(this.getSeoAttributes?.productDisplayName),
        productPrice: this.productPrice,
        discount: this.discount,
        currency: this.currencyCode,
      };
    },
    computedOptionsList() {
      if (window?.envSettings?.ITFE_PRODUCT_PAGE_ENABLE_REUSABLE_EMS_UI) {
        return this.productOptions.optionsList.value ?? [];
      }
      return this.getOptionsList ?? [];
    },
    analyticsIndex() {
      if (window?.envSettings?.ITFE_PRODUCT_PAGE_ENABLE_REUSABLE_EMS_UI) {
        return getSelectedEmsOptionIndex(this.productOptions.getOptionListByType('image'));
      }
      return getSelectedEmsOptionIndex(this.getOptionListByType('image'));
    },
  },
  watch: {
    shouldPinStickyATCButton() {
      const pageContainer = document.getElementById('app__product-page');
      pageContainer.style.marginBottom = '3rem';
    },
    async getPartNumber() {
      if (this.initialLoad) {
        this.initialLoad = false;
        this.initialUpdate();
      } else {
        await this.updateProductRoute();
        // Send analytics event when the product data is updated
        this.viewItemAnalyticsEvent();
      }
    },
  },
  mounted() {
    this.setHistoryListener();

    window.addEventListener('utag-ready', () => {
      this.viewItemAnalyticsEvent();
    });
    window.sendAnalyticsEvent = sendAnalyticsEvent;
  },
  beforeMount() {
    this.setBootstrap();
  },
  created() {
    if (this.$route.query?.pc) {
      VueCookies.set('GarminBuyPC', this.$route.query.pc, '1d', '/', '.garmin.com', true, 'None');
    }

    if (this.$route.query?.ticket) {
      const query = { ...this.$route.query };
      delete query.ticket;
      this.$router.replace({ query });
    }
  },
  methods: {
    ...mapActions([
      'setBootstrap',
      'updateProductData',
      'setProductActiveTabId',
    ]),
    appendDisclaimerToOverviewTab(overviewContent) {
      /*
      Appends the supplimentary disclaimers in the existing disclaimer container in the Overview tab
      */
      const containsDisclaimerBlock = overviewContent?.includes('<pc-disclaimer-block>');
      if (containsDisclaimerBlock) {
        // if the disclaimers are wrapped using PcDisclaimerBlock component
        const EODisclaimersIndex = overviewContent?.lastIndexOf('</pc-disclaimer-block>');
        const beforeEODContent = `${overviewContent.slice(0, EODisclaimersIndex)}</pc-disclaimer-block>`;
        const afterEODContent = overviewContent.slice(EODisclaimersIndex);
        return `${beforeEODContent}${this.markupDisclaimerBlock()}${afterEODContent}`;
      }
      // otherwise, the supplimentary disclaimers are appended at the end as items
      return `${overviewContent}${this.markupDisclaimerItems()}`;
    },
    markupDisclaimerBlock() {
      /*
      Builds and returns the HTML template of disclaimer block, which encapsulates all supplimentary disclaimers
      */
      let htmlContent = '<pc-disclaimer-block>';
      this.getSupplimentaryDisclaimers.forEach((disclaimer) => {
        htmlContent += convertImageSource(disclaimer)
          ? `<pc-disclaimer-icon
              image="${convertImageSource(disclaimer)}"
            />`
          : `<pc-disclaimer-item
              content="${disclaimer}"
            />\n`;
      });
      htmlContent += '</pc-disclaimer-block>\n';
      return htmlContent;
    },
    markupDisclaimerItems() {
      /*
      Builds and returns the HTML template of disclaimer items
      This is typically used in the old overview layout, where some disclaimers aren't wrapped in a PcDisclaimerBlock
      */
      let htmlContent = '';
      this.getSupplimentaryDisclaimers.forEach((disclaimer) => {
        htmlContent += convertImageSource(disclaimer)
          ? `<pc-disclaimer-icon
              image="${convertImageSource(disclaimer)}"
            />`
          : `<pc-disclaimer-item
              class='disclaimer-block'
              content="${disclaimer}"
            />\n`;
      });
      return htmlContent;
    },
    initialUpdate() {
      const { params } = this.$router.currentRoute.value;
      const newParams = {
        locale: this.getLocale,
        productId: this.getProductId,
        partNumber: this.getPartNumber,
      };

      if (
        (
          params.productId === newParams.productId
          && params.locale === newParams.locale
          && params.partNumber === newParams.partNumber
        )
        || (
          params.productId === newParams.productId
          && params.locale === newParams.locale
          && params.partNumber === undefined
        )
        || (
          params.productId === undefined
          && params.locale === newParams.locale
          && params.partNumber === newParams.partNumber
        )
      ) {
        return;
      }
      if (params.partNumber === undefined) {
        newParams.partNumber = undefined;
        this.$router.push({
          name: 'ShortPath',
          params: newParams,
          hash: this.$router.currentRoute.value?.hash,
        });
      } else if (params.productId === undefined) {
        newParams.productId = undefined;
        this.$router.push({
          name: 'FullPath',
          params: newParams,
          hash: this.$router.currentRoute.value?.hash,
        });
      } else {
        this.$router.push({
          name: 'FullPath',
          params: newParams,
          hash: this.$router.currentRoute.value?.hash,
        });
      }
    },
    async updateProductRoute() {
      const { params } = this.$router.currentRoute.value;
      const newParams = {
        locale: this.getLocale,
        productId: this.getProductId,
        partNumber: this.getPartNumber,
      };

      if (
        params.productId === newParams.productId
        && params.locale === newParams.locale
        && params.partNumber === newParams.partNumber
      ) {
        return;
      }
      await this.$router.push({
        name: 'FullPath',
        params: newParams,
        hash: this.$router.currentRoute.value?.hash,
        query: {},
      });
    },
    setHistoryListener() {
      window.addEventListener('popstate', () => {
        this.updateHistoryProductStates();
      });
    },
    updateHistoryProductStates() {
      const prevHash = this.getProductActiveTabId;
      const prevPartNumber = this.getPartNumber;
      const { hash, params } = this.$route;
      const { partNumber } = params;
      const newHash = hash.replace('#', '');

      const hashChanged = prevHash !== newHash;
      const productChanged = partNumber && partNumber !== prevPartNumber;
      const tabIsActiveTab = this.getIsActiveProductTab(newHash);
      const tabId = tabIsActiveTab ? newHash : this.getDefaultProductTab?.id;
      const setHash = tabIsActiveTab;

      if (hashChanged) this.setProductActiveTabId({ tabId, setHash });

      if (productChanged) this.updateProductData(partNumber);
    },
    getAnalyticsProductName() {
      return sanitizeAnalyticsString(this.getGlobalProductName) || 'NA';
    },
    getCategory3Value() {
      const productName = this.getAnalyticsProductName();
      return getCategory3Data(productName);
    },
    getAnalyticsProductPrice() {
      if (this.getPriceData?.salePrice?.price) {
        return [this.getPriceData.salePrice.price.toString()];
      }

      if (this.getPriceData?.listPrice?.price) {
        return [this.getPriceData.listPrice.price.toString()];
      }

      // If no sale price and no list price, return ['NA']
      return ['NA'];
    },
    viewItemAnalyticsEvent() {
      const commonAnalyticsData = {
        ...this.genericAnalyticsData(
          undefined,
          undefined,
        ),
        // Send the following fields as 'NA' for now since they are not available in the current context
        creative_name: 'NA',
        creative_slot: 'NA',
        location_id: 'NA',
        promotion_name: 'NA',
        promotion_id: 'NA',
        bundle_id: 'NA',
        is_bundle: 'NA',
        sale_type: 'NA',
        index: this.analyticsIndex,
        product_brand: window?.utag_data?.product_brand || ['NA'],
        product_category: window?.utag_data?.product_category || ['NA'],
        product_category2: window?.utag_data?.product_category2 || ['NA'],
        product_category3: this.getCategory3Value(),
        product_category4: getSelectedEmsOption(this.computedOptionsList, 0),
        product_category5: getSelectedEmsOption(this.computedOptionsList, 1),
        product_id: [this.getProductId || 'NA'],
        product_name: [this.getAnalyticsProductName()],
        product_original_price: this.getPriceData?.listPrice?.price ? [this.getPriceData?.listPrice?.price.toString()] : 'NA',
        product_current_price: this.getAnalyticsProductPrice(),
        item_family: this.getCategory3Value()?.[0] || 'NA',
        is_sale: this.getPriceData?.salePrice?.price ? 'yes' : 'no',
        price_shown: window.GarminAppBootstrap.skus[window.GarminAppBootstrap.sku].showPrice ? 'yes' : 'no',
        product_sku: this.getPartNumber ? [this.getPartNumber] : ['NA'],
        product_url: window?.location?.href ? [window.location.href.split('?')[0]] : ['NA'],
        product_variant: this.getProductVariation ? [sanitizeAnalyticsString(this.getProductVariation)] : ['NA'],
      };

      const pageViewAnalyticsData = {
        ...commonAnalyticsData,
        ...{ tealium_event: 'page_view' },
      };

      const viewItemAnalyticsData = {
        ...commonAnalyticsData,
        ...{ tealium_event: 'view_item' },
      };

      sendAnalyticsPageView(pageViewAnalyticsData);
      sendAnalyticsEvent(viewItemAnalyticsData);
    },
  },
};
</script>

<template>
  <div
    id="app-root"
  >
    <g-global-styles />
    <div
      id="global-live-region"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      class="visually-hidden"
    />
    <Interstitial
      v-if="getInterstitialIsVisible"
      :pv-name="getInterstitialProductPvName"
      :image-src="getInterstitialProductImage"
      :alt-text="getInterstitialProductAltText"
    />
    <div
      v-if="!getInterstitialIsVisible"
      id="app__product-page"
    >
      <main>
        <Breadcrumbs
          v-if="!isDesktopView"
          :crumbs="getBreadCrumbs"
          :analytics-data="breadCrumbAnalyticsData"
        />
        <section class="app__product">
          <ProductMediaGallery
            v-if="enableNewMediaGallery"
            :show-desktop-banner="isDesktopView"
            :currency-code="currencyCode"
            :product-id="getProductId"
            :part-number="getPartNumber"
            :product-name="getProductName"
            :price-data="getPriceData"
            :product-variation="getProductVariation"
            :promo-banner="getPromoBanner"
            :cloudinary-assets="getCloudinaryAssets"
            :customer-images="getCustomerImages"
            :videos-urls="getVideosURLs"
          />
          <CloudinaryProductGallery
            v-else
            :show-desktop-banner="isDesktopView"
            :currency-code="currencyCode"
            :product-id="getProductId"
            :part-number="getPartNumber"
            :product-name="getProductName"
            :price-data="getPriceData"
            :market-category-global="getMarketCategoryGlobal"
            :product-category-global="getProductCategoryGlobal"
            :product-variation="getProductVariation"
            :seo-attributes="getSeoAttributes"
            :promo-banner="getPromoBanner"
            :cloudinary-assets="getCloudinaryAssets"
            :cloud-name="getCloudinaryCloudName"
            :secure-distribution="getCloudinarySecureDistribution"
          />
          <ProductInfo
            v-if="enableEMSRedesign"
          />
          <OldProductInfo
            v-else
          />
        </section>
        <section
          v-if="showMobileBanner"
          class="app__banner__mobile"
        >
          <PcRenderPromo :content="getPromoBanner" />
        </section>
      </main>
      <aside>
        <Subnav
          :tab-labels="subNavLabels"
          :tab-content="subNavContent"
          :tab-ids="subNavIds"
          :product-variation="getProductVariation"
          :part-number="getPartNumber"
          :product-active-tab-id="getProductActiveTabId"
          :hash-is-valid-product-tab="getHashIsValidProductTab"
          :product-tabs-are-set="getProductTabsAreSet"
          :price-data="getPriceData"
          :product-id="getProductId"
          :seo-attributes="getSeoAttributes"
          :active-compatible-devices="getActiveCompatibleDevices"
          :product-url="getProductUrl"
          :customer-groups="getCustomerGroups"
          :currency-code="currencyCode"
        />
      </aside>
      <ATCButton
        :class="stickyAtcButtonClass"
        :style="pinnedToFooterStyle"
        :product-id="getProductId"
        :part-number="getPartNumber"
        :product-variation="getProductVariation"
        :show-sticky-add-to-cart-btn="enableStickyAddToCartBtn"
        :add-to-cart-href="getBuyActionHref"
        :add-to-cart-text="getBuyActionText"
      />
      <FakeATCButton
        :class="stickyFakeAtcButtonClass"
        :style="pinnedToFooterStyle"
        :product-id="getProductId"
        :part-number="getPartNumber"
        :product-variation="getProductVariation"
        :show-sticky-add-to-cart-btn="enableStickyFakeAddToCartBtn"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
* {
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
}

#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  /* stylelint-disable-next-line */
  color: #2c3e50;
}

#nav {
  padding: 30px;

  a {
    font-weight: bold;
    /* stylelint-disable-next-line */
    color: #2c3e50;

    &.router-link-exact-active {
      /* stylelint-disable-next-line */
      color: #42b983;
    }
  }
}

.app__product {
  position: relative;
  display: flex;
  flex-flow: row wrap;
  @include productBreakpoint(desktop-s) {
    margin: 1em 0;
    flex-flow: row nowrap;
  }

  &__sticky-atc-button,
  &__sticky-fake-atc-button {
    position: fixed;
    bottom: 0;
    width: calc(100% - 2rem);
    z-index: 100;
    margin: 1rem;

    :deep(a) {
      width: 100%;
    }

    p,
    /* stylelint-disable-next-line */
    g-button {
      margin: 0 1rem;
    }

    &--hidden {
      display: none;
    }
  }
}

.extra-space-for-button {
  margin-bottom: 3rem;
}

.pinned-to-footer {
  position: absolute;
  bottom: auto;
  top: auto;
  transform: translateY(-100%);
}

.app__banner {

  &__mobile {
    justify-content: center;
    display: flex;
    margin-bottom: 2em;
  }
}
</style>
